import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Label } from "@bcc/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { cn } from "@bcc/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { ADMIN_TARGET_WEB_STATUSES, adminTargetWebStatusesFrom, type AdminTargetWebStatus } from "@shared/types/admin-product-update";
import { useMutation } from "@tanstack/react-query";
import { format, startOfToday } from "date-fns";
import type { TFunction } from "i18next";
import { CalendarIcon, Loader2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Controller, useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type AdminStatusChangeValues, type EditableCatalogueRow, useAdminProductEdit } from "./admin-product-edit-context";
import {
  EDIT_CONTROL_CURSOR,
  EDITOR_OWNER_ATTRIBUTE,
  setAdminWebStatusEditTestId,
  shouldCloseOnOutsideInteraction,
} from "./admin-product-edit.utils";
import { useAdminUpdateToast } from "./admin-update-toast";
import { webStatusLabel } from "./admin-web-status";

const createSchema = (t: TFunction) =>
  z.object({
    webStatus: z.enum(ADMIN_TARGET_WEB_STATUSES),
    effectiveDate: z.date({ message: String(t("admin.productCatalogue.edit.validation.effectiveDateRequired")) }),
    comments: z
      .string()
      .trim()
      .min(1, String(t("admin.productCatalogue.edit.validation.commentsRequired"))),
  });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

/** Past dates are blocked; today is the earliest effective date. */
export const isBeforeToday = (date: Date): boolean => date < startOfToday();

// No inventory is entered: the API sends it as 0 for a Short Term SKU going out of stock, and not at all otherwise.
const toStatusChange = (values: FormValues): AdminStatusChangeValues => ({
  webStatus: values.webStatus,
  effectiveDate: format(values.effectiveDate, "yyyy-MM-dd"),
  comments: values.comments.trim(),
});

function EffectiveDateField({ form, editorId }: { readonly form: UseFormReturn<FormValues>; readonly editorId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      <Label className="text-xs">{t("admin.productCatalogue.edit.effectiveDate")}</Label>
      <Controller
        control={form.control}
        name="effectiveDate"
        render={({ field }) => (
          <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild testId={setAdminWebStatusEditTestId("date-trigger")}>
              <Button type="button" variant="outline" className={cn("w-full justify-start font-normal", EDIT_CONTROL_CURSOR)}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(field.value, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" {...{ [EDITOR_OWNER_ATTRIBUTE]: editorId }}>
              <Calendar
                mode="single"
                selected={field.value}
                defaultMonth={field.value}
                disabled={isBeforeToday}
                onSelect={(date) => {
                  if (date) field.onChange(date);
                  setOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        )}
      />
    </div>
  );
}

interface StatusChangeFieldsProps {
  readonly form: UseFormReturn<FormValues>;
  readonly options: AdminTargetWebStatus[];
  readonly editorId: string;
}

function StatusChangeFields({ form, options, editorId }: StatusChangeFieldsProps) {
  const { t } = useTranslation();
  const { errors } = form.formState;
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="admin-web-status" className="text-xs">
          {t("admin.productCatalogue.columns.webStatus")}
        </Label>
        <Controller
          control={form.control}
          name="webStatus"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                id="admin-web-status"
                className={cn("w-full", EDIT_CONTROL_CURSOR)}
                testId={setAdminWebStatusEditTestId("status-trigger")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent {...{ [EDITOR_OWNER_ATTRIBUTE]: editorId }}>
                {options.map((status) => (
                  <SelectItem
                    key={status}
                    className="cursor-pointer"
                    value={status}
                    testId={setAdminWebStatusEditTestId(`status-${status}`)}
                  >
                    {webStatusLabel(t, status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <EffectiveDateField form={form} editorId={editorId} />
      <div className="space-y-2">
        <Label htmlFor="admin-status-comments" className="text-xs">
          {t("admin.productCatalogue.edit.comments")}
        </Label>
        <Textarea
          id="admin-status-comments"
          rows={3}
          data-testid={setAdminWebStatusEditTestId("comments")}
          aria-invalid={errors.comments ? true : undefined}
          {...form.register("comments")}
        />
        {errors.comments ? <p className="text-xs text-destructive">{errors.comments.message}</p> : null}
      </div>
    </>
  );
}

interface WebStatusFormProps {
  readonly row: EditableCatalogueRow;
  readonly onClose: () => void;
  /** Told whether anything has been changed, so a click outside can keep or close the editor. */
  readonly onChangesChange: (hasChanges: boolean) => void;
  readonly editorId: string;
}

// Mounted only while the popover is open: a table of SKUs would otherwise hold a form and a mutation per row.
function WebStatusForm({ row, onClose, onChangesChange, editorId }: WebStatusFormProps) {
  const { t } = useTranslation();
  const edit = useAdminProductEdit();
  const { notifySuccess, notifyError } = useAdminUpdateToast();
  // Statuses that share the current one's Salesforce value would be a no-op request, so they aren't offered.
  const currentWebStatus = edit?.getStorefrontWebStatus(row);
  const options = useMemo(() => adminTargetWebStatusesFrom(currentWebStatus), [currentWebStatus]);
  const schema = useMemo(() => createSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    // "all" keeps isValid and every message current as the manager edits any field.
    mode: "all",
    defaultValues: { webStatus: options[0] ?? "Visible for Sale", effectiveDate: startOfToday(), comments: "" },
  });
  const mutation = useMutation({
    mutationFn: async (values: AdminStatusChangeValues) => {
      if (!edit) throw new Error("Admin product editing is not available");
      return await edit.submitStatusChange(row, values);
    },
    onSuccess: (response) => {
      notifySuccess(response);
      onClose();
    },
    // The toast explains the failure; the status stays editable for another try.
    onError: (error) => {
      notifyError(error);
      onClose();
    },
  });
  const { isDirty } = form.formState;
  useEffect(() => onChangesChange(isDirty), [isDirty, onChangesChange]);

  // Validate the empty form once so submit starts disabled with the required fields flagged.
  useEffect(() => {
    void form.trigger();
  }, [form]);

  return (
    <form onSubmit={form.handleSubmit((values) => mutation.mutate(toStatusChange(values)))} className="space-y-4">
      <div className="space-y-1">
        <h4 className="text-sm font-medium">{t("admin.productCatalogue.edit.statusTitle")}</h4>
        <p className="text-xs text-muted-foreground">
          {t("admin.productCatalogue.edit.statusDescription", { itemNumber: row.itemNumber, size: row.bagSize ?? "–" })}
        </p>
      </div>
      <StatusChangeFields form={form} options={options} editorId={editorId} />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn("flex-1", EDIT_CONTROL_CURSOR)}
          onClick={onClose}
          disabled={mutation.isPending}
          testId={setAdminWebStatusEditTestId("cancel")}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          size="sm"
          className={cn("flex-1", EDIT_CONTROL_CURSOR)}
          disabled={!form.formState.isValid || mutation.isPending}
          testId={setAdminWebStatusEditTestId("submit")}
        >
          {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t("admin.productCatalogue.edit.submitForApproval")}
        </Button>
      </div>
    </form>
  );
}

interface AdminWebStatusPopoverProps {
  readonly row: EditableCatalogueRow;
  readonly children: ReactNode;
}

/** Changes one SKU's web status in the selected region through an "Admin Status Change" approval request. */
export function AdminWebStatusPopover({ row, children }: AdminWebStatusPopoverProps) {
  const [open, setOpen] = useState(false);
  const editorId = useId();
  const hasChangesRef = useRef(false);
  const trackChanges = useCallback((hasChanges: boolean) => {
    hasChangesRef.current = hasChanges;
  }, []);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80"
        align="start"
        testId={setAdminWebStatusEditTestId("popover")}
        // Untouched, it closes like any popover; once anything is changed a stray click can't discard it.
        onInteractOutside={(event) => {
          if (!shouldCloseOnOutsideInteraction(event.target, hasChangesRef.current, editorId)) event.preventDefault();
        }}
      >
        <WebStatusForm row={row} onClose={() => setOpen(false)} onChangesChange={trackChanges} editorId={editorId} />
      </PopoverContent>
    </Popover>
  );
}
